'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { type DehydratedState, hashKey, hydrate, type QueryKey, useQueryClient } from '@tanstack/react-query';

export interface PendingInitialPrice {
  queryKey: QueryKey;
  state: Promise<DehydratedState>;
}

const PendingPriceContext = createContext<{ key?: string; pending: boolean }>({ pending: false });
const subscribe = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

/** Let the page render while its existing server price read finishes. */
export function InitialPriceHydration({ children, pending }: { children: ReactNode; pending?: PendingInitialPrice }) {
  const client = useQueryClient();
  const [settled, setSettled] = useState<Promise<DehydratedState>>();

  useEffect(() => {
    if (!pending) return;
    let active = true;
    void pending.state.then(
      (state) => {
        if (!active) return;
        // Hydration preserves the signed quote's original updatedAt and keeps
        // any newer browser result. Publish it before enabling a browser read.
        hydrate(client, state);
        setSettled(pending.state);
      },
      () => {
        // A broken response stream must still allow the normal browser fallback.
        if (active) setSettled(pending.state);
      },
    );
    return () => {
      active = false;
    };
  }, [client, pending]);

  const key = pending && hashKey(pending.queryKey);
  const waiting = Boolean(pending && settled !== pending.state);
  const value = useMemo(() => ({ key, pending: waiting }), [key, waiting]);
  return <PendingPriceContext value={value}>{children}</PendingPriceContext>;
}

export function useInitialPriceState(queryKey: QueryKey) {
  const initial = useContext(PendingPriceContext);
  const hydrated = useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);
  const matches = initial.key === hashKey(queryKey);
  // A later Suspense boundary must hydrate the same price-free markup it
  // received, even if the root has already received the streamed quote.
  return { pending: matches && initial.pending, hydrating: matches && !hydrated };
}
